import React, { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { PhoneCall, MessageSquare, Settings, FileText, Save, User, Video, Mic, MicOff, Camera, CameraOff } from "lucide-react";
import { useParams } from "react-router-dom";
const SOCKET_SERVER_URL = "https://backend-qureo-production-fab5.up.railway.app";
const API_BASE_URL = "https://backend-qureo-production-fab5.up.railway.app/api";

const hasTurnServer = (iceServers = []) => {
  return iceServers.some((server) => {
    const urls = Array.isArray(server?.urls) ? server.urls : [server?.urls];
    return urls.some((url) => typeof url === "string" && /^turns?:/i.test(url));
  });
};

const describeIceServers = (iceServers = []) => {
  return iceServers.map((server, index) => {
    const urls = Array.isArray(server?.urls) ? server.urls : [server?.urls];
    return {
      index,
      urls: urls.filter((url) => typeof url === "string"),
      hasUsername: Boolean(server?.username),
      hasCredential: Boolean(server?.credential),
    };
  });
};

const resolvePaidDurationMinutes = (consultationData) => {
  const candidates = [
    consultationData?.durationMinutes,
    consultationData?.duration,
    consultationData?.paidDurationMinutes,
    consultationData?.paidDuration,
    consultationData?.bookingDuration,
  ];

  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.round(parsed);
    }
  }

  return 30;
};

const DoctorCall = ({ consultationData }) => {

  
  // States
  const [inCall, setInCall] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [prescriptionOpen, setPrescriptionOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [callTime, setCallTime] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("Connecting...");
  const [saving, setSaving] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [patientConnected, setPatientConnected] = useState(false);
  
  // Prescription state
  const [prescription, setPrescription] = useState({
    medicines: [{ 
      name: "", 
      dosage: "", 
      frequency: "", 
      duration: "",
      instructions: "",
      quantity: 1,
      morning: false,
      afternoon: false,
      evening: false,
      night: false,
      beforeMeal: false,
      afterMeal: false
    }],
    instructions: "",
    followUpDate: "",
    labTests: [""],
    diagnosis: "",
    doctorNotes: ""
  });

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const socketRef = useRef(null);
  const iceQueue = useRef([]);
  const peerIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const localStreamRef = useRef(null);
  const hasTurnRef = useRef(false);
  const lastIceServersRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const autoExpireHandledRef = useRef(false);

  // Extract data
  const consultationId = consultationData?.id || consultationData?.consultationId || `121212`;
  const patientId = consultationData?.patientId || consultationData?.patient?._id;
  const patientName = consultationData?.patientName || consultationData?.patient?.name || "Patient";
  const doctorId = localStorage.getItem('doctorId') || localStorage.getItem('userId');
  const doctorName = localStorage.getItem('doctorName') || localStorage.getItem('userName') || "Doctor";
  const paidDurationMinutes = resolvePaidDurationMinutes(consultationData);
  const paidDurationSeconds = paidDurationMinutes * 60;
  const [remainingTime, setRemainingTime] = useState(paidDurationSeconds);

  // Configure axios (no token auth needed)
  useEffect(() => {
    axios.defaults.baseURL = API_BASE_URL;
  }, []);

  // ========== CHAT & PRESCRIPTION API FUNCTIONS ==========

  // Load chat history
  const loadChatHistory = async () => {
    if (!consultationId) {
      console.log("No consultation ID, skipping chat load");
      return;
    }

    setLoadingChat(true);
    try {
      console.log("Loading chat for consultation:", consultationId);
      
      const response = await axios.get(`/consultations/${consultationId}/chat`);
      
      if (response.data.success) {
        const loadedMessages = response.data.data?.chat || response.data.chat || [];
        console.log("Loaded chat messages:", loadedMessages.length);
        
        setMessages(loadedMessages);
      }
    } catch (error) {
      console.error("Error loading chat history:", error.response?.data || error.message);
      
      // Fallback mock data for development
      if (error.response?.status === 404 || error.response?.status === 401) {
        const mockMessages = [
          {
            _id: "1",
            text: "Hello Doctor, I'm not feeling well",
            sender: "patient",
            senderId: patientId,
            senderName: patientName,
            timestamp: new Date(Date.now() - 300000).toISOString(),
            type: "text"
          },
          {
            _id: "2",
            text: "Hello! What symptoms are you experiencing?",
            sender: "doctor",
            senderId: doctorId,
            senderName: "You",
            timestamp: new Date(Date.now() - 240000).toISOString(),
            type: "text"
          }
        ];
        setMessages(mockMessages);
      }
    } finally {
      setLoadingChat(false);
    }
  };

  // Save chat message to backend
  const saveChatMessage = async (message, type = 'text') => {
    if (!consultationId) {
      console.log("Cannot save chat - missing consultation ID");
      return;
    }

    try {
      const messageData = {
        message: message.text,
        senderType: message.sender,
        senderId: message.senderId,
        senderName: message.senderName,
        messageType: type
      };

      await axios.post(`/consultations/${consultationId}/chat/save`, messageData);
      console.log("✅ Chat message saved to backend");
    } catch (error) {
      console.error("Error saving chat message:", error.response?.data || error.message);
    }
  };

  // Send prescription to backend and patient
  const sendPrescription = async () => {
    if (!consultationId || !socketRef.current) {
      alert("Cannot send prescription - connection issue");
      return;
    }

    // Filter out empty medicines
    const validMedicines = prescription.medicines.filter(med => med.name.trim());
    if (validMedicines.length === 0) {
      alert("Please add at least one medicine");
      return;
    }

    setSaving(true);
    try {
      const prescriptionData = {
        medicines: validMedicines,
        instructions: prescription.instructions || '',
        followUpDate: prescription.followUpDate || null,
        labTests: prescription.labTests.filter(test => test.trim()),
        diagnosis: prescription.diagnosis || '',
        doctorNotes: prescription.doctorNotes || '',
        createdBy: doctorId
      };

      // 1. Save to backend
      const response = await axios.post(
        `/consultations/${consultationId}/prescription/create`,
        prescriptionData
      );

      console.log("✅ Prescription saved to backend:", response.data);

      // 2. Send via socket to patient
      socketRef.current.emit("prescription-from-doctor", {
        room: consultationId,
        prescription: response.data.data?.prescription || prescriptionData
      });

      // 3. Add system message to local chat
      const prescriptionMessage = {
        _id: `pres-${Date.now()}`,
        text: `You prescribed ${validMedicines.length} medicine(s)`,
        sender: "system",
        senderId: "system",
        senderName: "System",
        timestamp: new Date().toISOString(),
        type: "prescription",
        data: prescriptionData
      };

      setMessages(prev => [...prev, prescriptionMessage]);
      await saveChatMessage(prescriptionMessage, 'prescription');

      // 4. Reset prescription form
      setPrescription({
        medicines: [{ name: "", dosage: "", frequency: "", duration: "", instructions: "" }],
        instructions: "",
        followUpDate: "",
        labTests: [""],
        diagnosis: "",
        doctorNotes: ""
      });

      setPrescriptionOpen(false);
      alert("✅ Prescription sent successfully!");

    } catch (error) {
      console.error("Error sending prescription:", error.response?.data || error.message);
      alert("Failed to send prescription: " + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Save consultation summary
  const exportChatTranscript = () => {
    if (!Array.isArray(messages) || messages.length === 0) {
      alert("No chat messages to save yet.");
      return false;
    }

    const transcript = messages
      .map((msg) => {
        const timestamp = msg?.timestamp ? new Date(msg.timestamp).toLocaleString() : "Unknown time";
        const sender = msg?.senderName || msg?.sender || "Unknown";
        const text = msg?.text || "";
        return `[${timestamp}] ${sender}: ${text}`;
      })
      .join("\n");

    const blob = new Blob([transcript], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chat-transcript-${consultationId || "consultation"}-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  };

  const saveConsultationSummary = async () => {
    if (!consultationId) {
      alert("Cannot save consultation - missing consultation ID");
      return;
    }

    exportChatTranscript();

    setSaving(true);
    try {
      const summary = {
        callDuration: callTime,
        endedAt: new Date().toISOString(),
        consultationNotes: `Video consultation completed. Duration: ${callTime} seconds.`,
        keyFindings: [],
        recommendations: ["Follow prescription instructions", "Schedule follow-up if needed"],
        nextSteps: "Take prescribed medicines and monitor symptoms"
      };

      const response = await axios.post(
        `/consultations/${consultationId}/summary`,
        summary
      );

      if (response.data.success) {
        alert("✅ Consultation saved successfully!");
        console.log("Consultation saved:", response.data);
        
        // Redirect to doctor dashboard after 2 seconds
        setTimeout(() => {
          window.location.href = '/doctor/dashboard';
        }, 2000);
      }
    } catch (error) {
      console.error("Error saving consultation:", error.response?.data || error.message);
      alert("Failed to save consultation. Error: " + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const completeConsultationWhenExpired = async (elapsedCallTime) => {
    if (!consultationId) return;

    try {
      await axios.put(`/consultations/${consultationId}/complete`, {
        reason: "time_elapsed",
        endedAt: new Date().toISOString(),
        callDuration: elapsedCallTime,
      });
    } catch (error) {
      console.error("Error completing consultation after expiry:", error.response?.data || error.message);
    }
  };

  // ========== SOCKET.IO & WEBRTC SETUP ==========

  useEffect(() => {
    console.log("🚀 Initializing DoctorCall...");
    console.log("Consultation ID:", consultationId);
    console.log("Patient:", patientName);

    // Load chat history
    loadChatHistory();
    
    // Initialize socket connection
    const socket = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query: {
        userType: 'doctor',
        consultationId,
        doctorId
      }
    });
    
    socketRef.current = socket;

    // Socket event handlers
    socket.on("connect", () => {
      console.log("✅ Doctor connected to signaling server");
      setConnectionStatus("Connected, checking relay...");
      socket.emit("webrtc-join-room", consultationId);
    });

    socket.on("ice-servers", async (iceServers) => {
      console.log("Doctor received ICE servers:", describeIceServers(Array.isArray(iceServers) ? iceServers : []));
      lastIceServersRef.current = Array.isArray(iceServers) ? iceServers : [];
      hasTurnRef.current = hasTurnServer(lastIceServersRef.current);
      console.log("Doctor TURN available:", hasTurnRef.current, "ICE count:", lastIceServersRef.current.length);

      if (!hasTurnRef.current) {
        console.warn("Doctor TURN relay unavailable. Last ICE servers:", describeIceServers(lastIceServersRef.current));
        setConnectionStatus("TURN relay unavailable");
        alert("Secure relay (TURN) is unavailable. Calls across different networks may fail. Please try again later.");
        return;
      }

      setConnectionStatus("Relay ready");
      await initPeerConnection(lastIceServersRef.current);
    });

    socket.on("disconnect", () => {
      console.log("❌ Doctor disconnected from signaling server");
      setConnectionStatus("Disconnected");
      setInCall(false);
      setPatientConnected(false);
    });

    socket.on("connect_error", (error) => {
      console.error("❌ Socket connection error:", error);
      setConnectionStatus("Connection failed");
    });

    socket.on("user-joined", async ({ peerId }) => {
      console.log("👤 Patient joined:", peerId);
      setPatientConnected(true);
      peerIdRef.current = peerId;
      
      // Start call when patient joins
      if (!inCall && pcRef.current) {
        if (!hasTurnRef.current) {
          setConnectionStatus("TURN relay required");
          console.warn("Doctor blocked call start because TURN is unavailable.");
          return;
        }
        console.log("Patient joined, starting call...");
        await startCall(peerId);
      }
    });

    socket.on("user-left", ({ peerId }) => {
      console.log("👋 Patient left:", peerId);
      setPatientConnected(false);
      setInCall(false);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
    });

    socket.on("webrtc-answer", async ({ answer, from }) => {
      console.log("📥 Received answer from patient:", from);
      try {
        if (!pcRef.current) return;

        if (pcRef.current.signalingState === "have-local-offer") {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(answer));
          setInCall(true);
          console.log("✅ Call established with patient");
          await drainIceQueue();
        } else {
          console.warn("Ignoring answer - signaling state:", pcRef.current.signalingState);
        }
      } catch (err) {
        console.error("❌ Error handling answer:", err);
      }
    });

    socket.on("webrtc-ice-candidate", async ({ candidate, from }) => {
      console.log("🧊 Received ICE candidate from patient:", from);
      if (!candidate) return;
      
      if (!pcRef.current) {
        iceQueue.current.push(candidate);
        console.log("Queued ICE candidate");
        return;
      }
      
      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        console.log("✅ Added ICE candidate");
      } catch (err) {
        console.error("❌ Failed to add ICE candidate:", err);
      }
    });

    socket.on("chat-message", async ({ message, from }) => {
      console.log("💬 Chat message from patient:", message);
      
      const newMessageObj = {
        _id: `msg-${Date.now()}`,
        text: message,
        sender: "patient",
        senderId: from,
        senderName: patientName,
        timestamp: new Date().toISOString(),
        type: "text"
      };
      
      // Add to local state
      setMessages(prev => [...prev, newMessageObj]);
      
      // Increment unread count if chat is closed
      if (!chatOpen) {
        setUnreadMessages(prev => prev + 1);
      }
      
      // Save to backend
      await saveChatMessage(newMessageObj);
    });

    socket.on("prescription-sent", ({ success }) => {
      if (success) {
        console.log("✅ Prescription delivered to patient");
      }
    });

    return () => {
      console.log("🔴 Cleaning up Doctor socket");
      cleanup();
    };
  }, []);

  useEffect(() => {
    const nextDurationSeconds = resolvePaidDurationMinutes(consultationData) * 60;
    setRemainingTime(nextDurationSeconds);
    setCallTime(0);
    autoExpireHandledRef.current = false;
  }, [consultationData?.consultationId, consultationData?.id, consultationData?.durationMinutes, consultationData?.duration, consultationData?.paidDurationMinutes, consultationData?.paidDuration]);

  // ========== WEBRTC FUNCTIONS ==========

  const initPeerConnection = async (iceServersFromServer = []) => {
    try {
      console.log("Doctor initializing peer connection with ICE servers:", describeIceServers(iceServersFromServer));
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      console.log("✅ Doctor got local media stream");

      // Create peer connection
      if (!hasTurnServer(iceServersFromServer)) {
        console.warn("Doctor missing TURN relay in ICE servers:", describeIceServers(iceServersFromServer));
        throw new Error("TURN relay missing in ICE server list");
      }

      const pc = new RTCPeerConnection({
        iceServers: iceServersFromServer,
      });
      
      pcRef.current = pc;
      console.log("✅ Doctor created peer connection");

      // Add local tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Handle remote stream
      pc.ontrack = (event) => {
        console.log("🎬 Doctor received remote stream from patient");
        if (remoteVideoRef.current && event.streams[0]) {
          remoteVideoRef.current.srcObject = event.streams[0];
          setInCall(true);
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && peerIdRef.current) {
          socketRef.current.emit("webrtc-ice-candidate", {
            candidate: event.candidate,
            to: peerIdRef.current,
          });
        }
      };

      // Handle connection state
      pc.onconnectionstatechange = () => {
        console.log("Doctor connection state:", pc.connectionState);
        if (pc.connectionState === "connected") {
          setInCall(true);
          setConnectionStatus("Connected to patient");
        } else if (pc.connectionState === "disconnected" || pc.connectionState === "failed") {
          setInCall(false);
          setPatientConnected(false);
          setConnectionStatus("Disconnected from patient");
        }
      };

      // Handle ice connection state
      pc.oniceconnectionstatechange = () => {
        console.log("ICE connection state:", pc.iceConnectionState);
      };

    } catch (err) {
      console.error("❌ Doctor error initializing peer connection:", err);
      alert("Failed to access camera/microphone. Please check permissions.");
    }
  };

  const startCall = async (peerId) => {
    if (!pcRef.current) {
      console.error("Cannot start call - no peer connection");
      return;
    }

    if (!hasTurnRef.current) {
      setConnectionStatus("TURN relay required");
      console.warn("Cannot start call without TURN relay.");
      return;
    }

    try {
      console.log("📞 Starting call to patient:", peerId);
      
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);
      
      socketRef.current.emit("webrtc-offer", { 
        offer, 
        to: peerId 
      });
      
      console.log("✅ Offer sent to patient");
      
    } catch (err) {
      console.error("❌ Error starting call:", err);
    }
  };

  const drainIceQueue = async () => {
    if (!pcRef.current || iceQueue.current.length === 0) return;
    
    console.log("Draining ICE queue:", iceQueue.current.length);
    
    for (const candidate of iceQueue.current) {
      try {
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error("Error adding queued ICE candidate:", err);
      }
    }
    
    iceQueue.current = [];
  };

  // ========== CALL TIMER ==========

  useEffect(() => {
    if (!inCall || remainingTime <= 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      setCallTime((prev) => prev + 1);
      setRemainingTime((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [inCall, remainingTime]);

  useEffect(() => {
    if (!inCall || remainingTime > 0 || autoExpireHandledRef.current) {
      return;
    }

    autoExpireHandledRef.current = true;

    const expiryMessage = {
      _id: `expiry-${Date.now()}`,
      text: "Your time booked has expired.",
      sender: "system",
      senderId: "system",
      senderName: "System",
      timestamp: new Date().toISOString(),
      type: "system"
    };

    setMessages((prev) => [...prev, expiryMessage]);
    if (!chatOpen) {
      setUnreadMessages((prev) => prev + 1);
    }

    saveChatMessage(expiryMessage, "system");
    completeConsultationWhenExpired(callTime);

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    setInCall(false);
    setPatientConnected(false);
    setConnectionStatus("Consultation completed");
  }, [inCall, remainingTime, chatOpen, callTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // ========== CHAT FUNCTIONS ==========

  const sendMessage = () => {
    if (!newMessage.trim() || !socketRef.current) return;
    
    const messageObj = {
      _id: `msg-${Date.now()}`,
      text: newMessage,
      sender: "doctor",
      senderId: doctorId,
      senderName: "You",
      timestamp: new Date().toISOString(),
      type: "text"
    };
    
    // Send via socket to patient
    socketRef.current.emit("chat-message", { 
      room: consultationId, 
      message: newMessage,
      senderType: "doctor",
      senderId: doctorId,
      senderName: doctorName
    });
    
    // Add to local messages
    setMessages(prev => [...prev, messageObj]);
    setNewMessage("");
    
    // Save to backend
    saveChatMessage(messageObj);
  };

  // ========== PRESCRIPTION FUNCTIONS ==========

  const handlePrescriptionChange = (index, field, value) => {
    const newMedicines = [...prescription.medicines];
    newMedicines[index][field] = value;
    setPrescription({ ...prescription, medicines: newMedicines });
  };

  const addMedicine = () => {
    setPrescription({
      ...prescription,
      medicines: [...prescription.medicines, { 
        name: "", 
        dosage: "", 
        frequency: "", 
        duration: "",
        instructions: "",
        quantity: 1,
        morning: false,
        afternoon: false,
        evening: false,
        night: false,
        beforeMeal: false,
        afterMeal: false
      }]
    });
  };

  const removeMedicine = (index) => {
    const newMedicines = prescription.medicines.filter((_, i) => i !== index);
    setPrescription({ ...prescription, medicines: newMedicines });
  };

  const addLabTest = () => {
    setPrescription({
      ...prescription,
      labTests: [...prescription.labTests, ""]
    });
  };

  const removeLabTest = (index) => {
    const newLabTests = prescription.labTests.filter((_, i) => i !== index);
    setPrescription({ ...prescription, labTests: newLabTests });
  };

  // ========== MEDIA CONTROLS ==========

  const toggleMic = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !micEnabled;
      });
      setMicEnabled(!micEnabled);
    }
  };

  const toggleCam = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = !camEnabled;
      });
      setCamEnabled(!camEnabled);
    }
  };

  // ========== CLEANUP ==========

  const cleanup = () => {
    console.log("🧹 Doctor cleaning up...");

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Save consultation before leaving
    if (consultationId && messages.length > 0) {
      saveConsultationSummary();
    }
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setInCall(false);
    setConnectionStatus("Disconnected");
    setPatientConnected(false);
    peerIdRef.current = null;
    iceQueue.current = [];
  };

  // ========== AUTO-SCROLL CHAT ==========

  useEffect(() => {
    if (messagesEndRef.current && chatOpen) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, chatOpen]);

  // Reset unread when opening chat
  useEffect(() => {
    if (chatOpen) {
      setUnreadMessages(0);
    }
  }, [chatOpen]);

  // ========== RENDER ==========

  return (
    <div style={{ 
      width: "100vw", 
      height: "100vh", 
      backgroundColor: "#0a0a0a",
      color: "white",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>
      
      {/* Header */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        padding: "15px 25px",
        backgroundColor: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div>
          <h3 style={{ margin: 0, fontWeight: 600 }}>Dr. {doctorName}</h3>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "10px",
            fontSize: "14px",
            marginTop: "5px"
          }}>
            <span style={{ 
              padding: "3px 8px", 
              borderRadius: "12px",
              backgroundColor: patientConnected ? "#10b981" : "#ef4444",
              fontSize: "12px"
            }}>
              {patientConnected ? "Patient Connected" : "Waiting for Patient"}
            </span>
            <span style={{ color: "#9ca3af" }}>
              Consultation ID: {consultationId.substring(0, 8)}...
            </span>
          </div>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          {inCall && (
            <div style={{
              backgroundColor: "rgba(0,0,0,0.5)",
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "14px",
              border: "1px solid rgba(255,255,255,0.2)"
            }}>
              ⏱️ {formatTime(remainingTime)}
            </div>
          )}
          
          <div style={{
            padding: "6px 12px",
            borderRadius: "20px",
            fontSize: "14px",
            backgroundColor: connectionStatus.includes("Connected") ? "#10b981" : "#ef4444"
          }}>
            {connectionStatus}
          </div>
        </div>
      </div>

      {/* Main Video Area */}
      <div style={{
        position: "absolute",
        top: "80px",
        bottom: "120px",
        left: "20px",
        right: "20px",
        display: "flex",
        gap: "20px"
      }}>
        
        {/* Patient Video (Large) */}
        <div style={{
          flex: 3,
          backgroundColor: "#111",
          borderRadius: "15px",
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 10px 25px rgba(0,0,0,0.5)"
        }}>
          <video 
            ref={remoteVideoRef} 
            autoPlay 
            playsInline 
            style={{ 
              width: "100%", 
              height: "100%", 
              objectFit: "cover",
              backgroundColor: "#111"
            }} 
          />
          
          {!patientConnected && (
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.8)"
            }}>
              <div style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                border: "3px solid #4f46e5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px"
              }}>
                <User size={40} color="#4f46e5" />
              </div>
              <h3 style={{ margin: 0, marginBottom: "10px" }}>{patientName}</h3>
              <p style={{ color: "#9ca3af", margin: 0 }}>
                Waiting for patient to join the call...
              </p>
            </div>
          )}
          
          {/* Patient info overlay */}
          {patientConnected && (
            <div style={{
              position: "absolute",
              bottom: "20px",
              left: "20px",
              backgroundColor: "rgba(0,0,0,0.7)",
              padding: "10px 15px",
              borderRadius: "10px",
              backdropFilter: "blur(10px)"
            }}>
              <div style={{ fontWeight: 600 }}>{patientName}</div>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>Patient</div>
            </div>
          )}
        </div>
        
        {/* Doctor Video (Small) & Controls */}
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "20px"
        }}>
          {/* Doctor's self view */}
          <div style={{
            backgroundColor: "#111",
            borderRadius: "15px",
            overflow: "hidden",
            position: "relative",
            height: "200px",
            boxShadow: "0 5px 15px rgba(0,0,0,0.3)"
          }}>
            <video 
              ref={localVideoRef} 
              autoPlay 
              muted 
              playsInline 
              style={{ 
                width: "100%", 
                height: "100%", 
                objectFit: "cover",
                transform: "scaleX(-1)"
              }} 
            />
            <div style={{
              position: "absolute",
              bottom: "10px",
              left: "10px",
              backgroundColor: "rgba(0,0,0,0.7)",
              padding: "5px 10px",
              borderRadius: "8px",
              fontSize: "12px",
              backdropFilter: "blur(10px)"
            }}>
              You
            </div>
          </div>
          
          {/* Quick Stats */}
          <div style={{
            backgroundColor: "rgba(30, 41, 59, 0.8)",
            borderRadius: "15px",
            padding: "20px",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.1)"
          }}>
            <h4 style={{ margin: "0 0 15px 0", fontSize: "16px" }}>Consultation Stats</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>Time Left</div>
                <div style={{ fontSize: "20px", fontWeight: 600 }}>{formatTime(remainingTime)}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>Messages</div>
                <div style={{ fontSize: "20px", fontWeight: 600 }}>{messages.length}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>Medicines</div>
                <div style={{ fontSize: "20px", fontWeight: 600 }}>
                  {prescription.medicines.filter(m => m.name.trim()).length}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>Status</div>
                <div style={{ 
                  fontSize: "14px", 
                  fontWeight: 600,
                  color: inCall ? "#10b981" : "#ef4444"
                }}>
                  {inCall ? "Active" : "Waiting"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: "20px",
        backgroundColor: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(10px)",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "20px",
        zIndex: 1000
      }}>
        
        {/* Media Controls */}
        <div style={{ display: "flex", gap: "10px" }}>
          <button 
            onClick={toggleMic}
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              border: "none",
              backgroundColor: micEnabled ? "#3b82f6" : "#ef4444",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s"
            }}
            title={micEnabled ? "Mute Microphone" : "Unmute Microphone"}
          >
            {micEnabled ? <Mic size={24} /> : <MicOff size={24} />}
          </button>
          
          <button 
            onClick={toggleCam}
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              border: "none",
              backgroundColor: camEnabled ? "#3b82f6" : "#ef4444",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s"
            }}
            title={camEnabled ? "Turn Camera Off" : "Turn Camera On"}
          >
            {camEnabled ? <Camera size={24} /> : <CameraOff size={24} />}
          </button>
        </div>
        
        {/* Main Controls */}
        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          {/* Prescription Button */}
          <button
            onClick={() => setPrescriptionOpen(!prescriptionOpen)}
            style={{
              padding: "12px 20px",
              borderRadius: "25px",
              border: "none",
              backgroundColor: "#8b5cf6",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              transition: "all 0.2s"
            }}
          >
            <FileText size={20} />
            Prescription
          </button>
          
          {/* Chat Button */}
          <button
            onClick={() => setChatOpen(!chatOpen)}
            style={{
              padding: "12px 20px",
              borderRadius: "25px",
              border: "none",
              backgroundColor: "#7b3aed",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              position: "relative",
              transition: "all 0.2s"
            }}
          >
            <MessageSquare size={20} />
            Chat
            {unreadMessages > 0 && (
              <span style={{
                position: "absolute",
                top: "-5px",
                right: "-5px",
                backgroundColor: "#ef4444",
                color: "white",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
                {unreadMessages}
              </span>
            )}
          </button>
          
          {/* Save Button */}
          <button
            onClick={saveConsultationSummary}
            disabled={saving}
            style={{
              padding: "12px 20px",
              borderRadius: "25px",
              border: "none",
              backgroundColor: saving ? "#6b7280" : "#059669",
              color: "white",
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              transition: "all 0.2s"
            }}
          >
            <Save size={20} />
            {saving ? "Saving..." : "Save & End"}
          </button>
          
          {/* End Call Button */}
          <button
            onClick={cleanup}
            style={{
              padding: "15px 25px",
              borderRadius: "25px",
              border: "none",
              backgroundColor: "#dc2626",
              color: "white",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontWeight: 600,
              fontSize: "16px",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(220, 38, 38, 0.4)"
            }}
          >
            <PhoneCall size={22} />
            End Call
          </button>
        </div>
        
        {/* Settings Button */}
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          style={{
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "#374151",
            color: "white",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s"
          }}
          title="Settings"
        >
          <Settings size={24} />
        </button>
      </div>

      {/* Chat Panel */}
      {chatOpen && (
        <div style={{
          position: "absolute",
          bottom: "120px",
          right: "20px",
          width: "400px",
          height: "500px",
          backgroundColor: "#111827",
          borderRadius: "15px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          zIndex: 2000,
          border: "1px solid rgba(255,255,255,0.1)"
        }}>
          {/* Chat Header */}
          <div style={{
            padding: "20px",
            backgroundColor: "#1e293b",
            borderBottom: "1px solid rgba(255,255,255,0.1)"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: "18px" }}>💬 Chat with {patientName}</h3>
              <button
                onClick={() => setChatOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#9ca3af",
                  cursor: "pointer",
                  fontSize: "20px"
                }}
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Messages Container */}
          <div style={{
            flex: 1,
            padding: "20px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "15px",
            backgroundColor: "#0f172a"
          }}>
            {loadingChat ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                Loading chat history...
              </div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg._id}
                  style={{
                    alignSelf: msg.sender === "doctor" ? "flex-end" : "flex-start",
                    maxWidth: "80%"
                  }}
                >
                  {msg.type === "prescription" ? (
                    <div style={{
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                      padding: "12px 15px",
                      borderRadius: "15px",
                      border: "1px solid #fbbf24",
                      fontSize: "14px"
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: "5px" }}>
                        💊 Prescription Sent
                      </div>
                      <div>{msg.text}</div>
                    </div>
                  ) : (
                    <div style={{
                      backgroundColor: msg.sender === "doctor" ? "#3b82f6" : "#374151",
                      color: "white",
                      padding: "12px 15px",
                      borderRadius: "15px",
                      borderBottomRightRadius: msg.sender === "doctor" ? "5px" : "15px",
                      borderBottomLeftRadius: msg.sender === "doctor" ? "15px" : "5px"
                    }}>
                      <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "5px" }}>
                        {msg.senderName}
                      </div>
                      <div>{msg.text}</div>
                      <div style={{
                        fontSize: "11px",
                        opacity: 0.7,
                        marginTop: "8px",
                        textAlign: "right"
                      }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Message Input */}
          <div style={{
            padding: "15px 20px",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            backgroundColor: "#1e293b"
          }}>
            <div style={{ display: "flex", gap: "10px" }}>
              <input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  padding: "12px 15px",
                  borderRadius: "25px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "#0f172a",
                  color: "white",
                  fontSize: "14px",
                  outline: "none"
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!newMessage.trim()}
                style={{
                  padding: "12px 25px",
                  borderRadius: "25px",
                  border: "none",
                  backgroundColor: newMessage.trim() ? "#7b3aed" : "#4b5563",
                  color: "white",
                  cursor: newMessage.trim() ? "pointer" : "not-allowed",
                  fontWeight: 600,
                  fontSize: "14px",
                  transition: "all 0.2s"
                }}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prescription Panel */}
      {prescriptionOpen && (
        <div style={{
          position: "absolute",
          bottom: "120px",
          left: "20px",
          width: "500px",
          maxHeight: "600px",
          backgroundColor: "#111827",
          borderRadius: "15px",
          overflow: "hidden",
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
          display: "flex",
          flexDirection: "column",
          zIndex: 2000,
          border: "1px solid rgba(255,255,255,0.1)"
        }}>
          {/* Header */}
          <div style={{
            padding: "20px",
            backgroundColor: "#1e293b",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <h3 style={{ margin: 0, fontSize: "18px", display: "flex", alignItems: "center", gap: "10px" }}>
              <FileText size={24} />
              Create Prescription
            </h3>
            <button
              onClick={() => setPrescriptionOpen(false)}
              style={{
                background: "none",
                border: "none",
                color: "#9ca3af",
                cursor: "pointer",
                fontSize: "20px"
              }}
            >
              ×
            </button>
          </div>
          
          {/* Prescription Form */}
          <div style={{
            flex: 1,
            padding: "20px",
            overflowY: "auto",
            backgroundColor: "#0f172a"
          }}>
            {/* Diagnosis */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", color: "#9ca3af" }}>
                Diagnosis
              </label>
              <textarea
                value={prescription.diagnosis}
                onChange={(e) => setPrescription({...prescription, diagnosis: e.target.value})}
                placeholder="Enter diagnosis..."
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "#1e293b",
                  color: "white",
                  fontSize: "14px",
                  minHeight: "80px",
                  resize: "vertical"
                }}
              />
            </div>
            
            {/* Medicines */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <h4 style={{ margin: 0, fontSize: "16px" }}>Medicines</h4>
                <button
                  onClick={addMedicine}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "#10b981",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                >
                  + Add Medicine
                </button>
              </div>
              
              {prescription.medicines.map((med, index) => (
                <div key={index} style={{
                  marginBottom: "15px",
                  padding: "15px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "10px",
                  backgroundColor: "#1e293b"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <div style={{ fontWeight: 600 }}>Medicine {index + 1}</div>
                    {prescription.medicines.length > 1 && (
                      <button
                        onClick={() => removeMedicine(index)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
                    <div>
                      <input
                        type="text"
                        placeholder="Medicine Name *"
                        value={med.name}
                        onChange={(e) => handlePrescriptionChange(index, 'name', e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backgroundColor: "#0f172a",
                          color: "white",
                          fontSize: "14px"
                        }}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Dosage (e.g., 500mg)"
                        value={med.dosage}
                        onChange={(e) => handlePrescriptionChange(index, 'dosage', e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backgroundColor: "#0f172a",
                          color: "white",
                          fontSize: "14px"
                        }}
                      />
                    </div>
                  </div>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div>
                      <input
                        type="text"
                        placeholder="Frequency (e.g., 2 times daily)"
                        value={med.frequency}
                        onChange={(e) => handlePrescriptionChange(index, 'frequency', e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backgroundColor: "#0f172a",
                          color: "white",
                          fontSize: "14px"
                        }}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Duration (e.g., 7 days)"
                        value={med.duration}
                        onChange={(e) => handlePrescriptionChange(index, 'duration', e.target.value)}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.2)",
                          backgroundColor: "#0f172a",
                          color: "white",
                          fontSize: "14px"
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Instructions */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", color: "#9ca3af" }}>
                Instructions for Patient
              </label>
              <textarea
                value={prescription.instructions}
                onChange={(e) => setPrescription({...prescription, instructions: e.target.value})}
                placeholder="Enter instructions for the patient..."
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "#1e293b",
                  color: "white",
                  fontSize: "14px",
                  minHeight: "80px",
                  resize: "vertical"
                }}
              />
            </div>
            
            {/* Follow-up Date */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", color: "#9ca3af" }}>
                Follow-up Date
              </label>
              <input
                type="date"
                value={prescription.followUpDate}
                onChange={(e) => setPrescription({...prescription, followUpDate: e.target.value})}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "#1e293b",
                  color: "white",
                  fontSize: "14px"
                }}
              />
            </div>
            
            {/* Lab Tests */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                <h4 style={{ margin: 0, fontSize: "16px" }}>Recommended Lab Tests</h4>
                <button
                  onClick={addLabTest}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "#3b82f6",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                >
                  + Add Test
                </button>
              </div>
              
              {prescription.labTests.map((test, index) => (
                <div key={index} style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                  <input
                    type="text"
                    placeholder="Lab test name"
                    value={test}
                    onChange={(e) => {
                      const newLabTests = [...prescription.labTests];
                      newLabTests[index] = e.target.value;
                      setPrescription({...prescription, labTests: newLabTests});
                    }}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid rgba(255,255,255,0.2)",
                      backgroundColor: "#1e293b",
                      color: "white",
                      fontSize: "14px"
                    }}
                  />
                  {prescription.labTests.length > 1 && (
                    <button
                      onClick={() => removeLabTest(index)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: "#ef4444",
                        color: "white",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Doctor Notes */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", color: "#9ca3af" }}>
                Doctor's Notes
              </label>
              <textarea
                value={prescription.doctorNotes}
                onChange={(e) => setPrescription({...prescription, doctorNotes: e.target.value})}
                placeholder="Additional notes..."
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  backgroundColor: "#1e293b",
                  color: "white",
                  fontSize: "14px",
                  minHeight: "60px",
                  resize: "vertical"
                }}
              />
            </div>
          </div>
          
          {/* Footer */}
          <div style={{
            padding: "20px",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            backgroundColor: "#1e293b",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px"
          }}>
            <button
              onClick={() => setPrescriptionOpen(false)}
              style={{
                padding: "10px 20px",
                borderRadius: "6px",
                border: "1px solid rgba(255,255,255,0.2)",
                backgroundColor: "transparent",
                color: "#9ca3af",
                cursor: "pointer",
                fontSize: "14px"
              }}
            >
              Cancel
            </button>
            <button
              onClick={sendPrescription}
              disabled={saving || prescription.medicines.filter(m => m.name.trim()).length === 0}
              style={{
                padding: "10px 30px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: saving ? "#6b7280" : "#10b981",
                color: "white",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "14px"
              }}
            >
              {saving ? "Sending..." : "Send Prescription"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorCall;